/**
 * API client for HeizOS CRM
 * Wraps fetch calls with auth token injection
 * Used by Zustand store to sync state with PostgreSQL
 */

import { emitToast } from '@/components/ui/Toast'

const API_BASE = '/api'

/** Mutacje, po których pokazujemy globalny feedback (toast).
    POST wyłączony globalnie (login/wyszukiwania) — sukcesy dla create dodajemy per-akcja. */
const MUTATING = new Set(['PATCH', 'PUT', 'DELETE'])

export function getToken(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = localStorage.getItem('heizos-auth')
    if (stored) {
      const parsed = JSON.parse(stored)
      return parsed?.state?.token || null
    }
  } catch {
    // ignore
  }
  return null
}

async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken()

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  })

  if (res.status === 401) {
    // Token expired — clear auth and reload
    if (typeof window !== 'undefined') {
      localStorage.removeItem('heizos-auth')
      window.location.href = '/installer'
    }
    throw new Error('Unauthorized')
  }

  const method = (options.method || 'GET').toUpperCase()

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: res.statusText }))
    const msg = errorData.error || `API Error ${res.status}`
    // Koniec z połykaniem błędów: każda nieudana operacja mówi to userowi
    emitToast(
      method === 'GET' ? 'Nie udało się pobrać danych' : `Nie udało się zapisać: ${msg}`,
      'error'
    )
    throw new Error(msg)
  }

  if (MUTATING.has(method)) {
    emitToast(method === 'DELETE' ? 'Usunięto' : 'Zapisano', 'success')
  }

  return res.json()
}

/* ──────────────────────────────────────────
   AUTH
   ────────────────────────────────────────── */

export const authApi = {
  login: (email: string, password: string) =>
    apiFetch<{
      token: string
      user: { id: string; name: string; email: string; role: string; company: string; companyId: string }
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () =>
    apiFetch<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  me: () =>
    apiFetch<{
      user: { id: string; name: string; email: string; role: string; company: string; companyId: string }
    }>('/auth/me'),
}

/* ──────────────────────────────────────────
   DEALS
   ────────────────────────────────────────── */

export const dealsApi = {
  list: () => apiFetch<DealResponse[]>('/crm/deals'),

  get: (id: string) => apiFetch<DealResponse>(`/crm/deals/${id}`),

  create: (data: Record<string, unknown>) =>
    apiFetch<DealResponse>('/crm/deals', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<DealResponse>(`/crm/deals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<{ ok: boolean }>(`/crm/deals/${id}`, { method: 'DELETE' }),

  addHistory: (dealId: string, data: { type: string; description: string; auto?: boolean }) =>
    apiFetch(`/crm/deals/${dealId}/history`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  addDocument: (dealId: string, data: { name: string; type: string; url: string }) =>
    apiFetch(`/crm/deals/${dealId}/documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteDocument: (dealId: string, docId: string) =>
    apiFetch(`/crm/deals/${dealId}/documents?docId=${docId}`, { method: 'DELETE' }),

  // Offer items on deal
  getOfferItems: (dealId: string) =>
    apiFetch<OfferItemResponse[]>(`/crm/deals/${dealId}/offer-items`),

  addOfferItem: (dealId: string, data: Record<string, unknown>) =>
    apiFetch(`/crm/deals/${dealId}/offer-items`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  replaceOfferItems: (dealId: string, items: unknown[]) =>
    apiFetch(`/crm/deals/${dealId}/offer-items`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    }),
}

/* ──────────────────────────────────────────
   OFFERS
   ────────────────────────────────────────── */

export const offersApi = {
  list: (dealId?: string) =>
    apiFetch<OfferResponse[]>(`/crm/offers${dealId ? `?dealId=${dealId}` : ''}`),

  get: (id: string) => apiFetch<OfferResponse>(`/crm/offers/${id}`),

  create: (data: Record<string, unknown>) =>
    apiFetch<OfferResponse>('/crm/offers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<OfferResponse>(`/crm/offers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<{ ok: boolean }>(`/crm/offers/${id}`, { method: 'DELETE' }),

  nextNumber: () =>
    apiFetch<{ offerNumber: string }>('/crm/offers/next-number'),
}

/* ──────────────────────────────────────────
   PRODUCTS
   ────────────────────────────────────────── */

export const productsApi = {
  list: () => apiFetch<ProductResponse[]>('/crm/products'),

  create: (data: Record<string, unknown>) =>
    apiFetch<ProductResponse>('/crm/products', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<ProductResponse>(`/crm/products/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<{ ok: boolean }>(`/crm/products/${id}`, { method: 'DELETE' }),
}

/* ──────────────────────────────────────────
   CATEGORIES
   ────────────────────────────────────────── */

export const categoriesApi = {
  list: () => apiFetch<CategoryResponse[]>('/crm/categories'),

  create: (label: string) =>
    apiFetch<CategoryResponse>('/crm/categories', {
      method: 'POST',
      body: JSON.stringify({ label }),
    }),

  update: (id: string, label: string) =>
    apiFetch<CategoryResponse>(`/crm/categories?id=${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ label }),
    }),

  delete: (id: string) =>
    apiFetch<{ ok: boolean }>(`/crm/categories?id=${id}`, { method: 'DELETE' }),
}

/* ──────────────────────────────────────────
   PARTNERS
   ────────────────────────────────────────── */

export const partnersApi = {
  list: () => apiFetch<PartnerResponse[]>('/crm/partners'),

  create: (name: string, logoUrl?: string) =>
    apiFetch<PartnerResponse>('/crm/partners', {
      method: 'POST',
      body: JSON.stringify({ name, logoUrl }),
    }),

  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<PartnerResponse>(`/crm/partners/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<{ ok: boolean }>(`/crm/partners/${id}`, { method: 'DELETE' }),
}

/* ──────────────────────────────────────────
   TEMPLATES
   ────────────────────────────────────────── */

export const templatesApi = {
  list: () => apiFetch<TemplateResponse[]>('/crm/templates'),

  create: (data: { name: string; description: string; items: unknown[] }) =>
    apiFetch<TemplateResponse>('/crm/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<TemplateResponse>(`/crm/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<{ ok: boolean }>(`/crm/templates/${id}`, { method: 'DELETE' }),
}

/* ──────────────────────────────────────────
   SETTINGS
   ────────────────────────────────────────── */

export const settingsApi = {
  get: () => apiFetch<SettingsResponse>('/crm/settings'),

  update: (data: Record<string, unknown>) =>
    apiFetch<SettingsResponse>('/crm/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
}

/* ──────────────────────────────────────────
   USERS (admin)
   ────────────────────────────────────────── */

export const usersApi = {
  list: () => apiFetch<UserResponse[]>('/crm/users'),

  create: (data: { name: string; email: string; password: string; role: string }) =>
    apiFetch<UserResponse>('/crm/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<UserResponse>(`/crm/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<{ ok: boolean }>(`/crm/users/${id}`, { method: 'DELETE' }),
}

/* ──────────────────────────────────────────
   SERVICE ORDERS
   ────────────────────────────────────────── */

export const serviceApi = {
  list: (params?: { status?: string; dealId?: string }) => {
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.dealId) query.set('dealId', params.dealId)
    const qs = query.toString()
    return apiFetch<ServiceOrderResponse[]>(`/crm/service${qs ? '?' + qs : ''}`)
  },

  // Server-side filtered + paginated list (for the scalable List view).
  listPaged: (params: {
    page: number; limit?: number; q?: string; bucket?: string; type?: string
    technicianId?: string; from?: string; to?: string; sort?: string; dir?: string; archived?: boolean
  }) => {
    const query = new URLSearchParams()
    query.set('page', String(params.page))
    if (params.limit) query.set('limit', String(params.limit))
    if (params.q) query.set('q', params.q)
    if (params.bucket && params.bucket !== 'all') query.set('bucket', params.bucket)
    if (params.type && params.type !== 'all') query.set('type', params.type)
    if (params.technicianId) query.set('technicianId', params.technicianId)
    if (params.from) query.set('from', params.from)
    if (params.to) query.set('to', params.to)
    if (params.sort) query.set('sort', params.sort)
    if (params.dir) query.set('dir', params.dir)
    if (params.archived) query.set('archived', '1')
    return apiFetch<{ orders: ServiceOrderResponse[]; total: number; page: number; limit: number }>(
      `/crm/service?${query.toString()}`
    )
  },

  get: (id: string) =>
    apiFetch<ServiceOrderResponse>(`/crm/service/${id}`),

  create: (data: Record<string, unknown>) =>
    apiFetch<ServiceOrderResponse>('/crm/service', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<ServiceOrderResponse>(`/crm/service/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<{ ok: boolean }>(`/crm/service/${id}`, { method: 'DELETE' }),

  // ─── Photos (Drive subfolder per order) ───────────────────
  listPhotos: (id: string) =>
    apiFetch<{
      serviceFolderUrl: string | null
      photos: Array<{
        id: string; name: string; mimeType: string
        webViewLink: string; thumbnailLink?: string
        createdTime: string; size?: string
      }>
    }>(`/crm/service/${id}/photos`),

  // Ensure/resolve the service order Drive folder ON DEMAND (no photo needed).
  ensureFolder: (id: string) =>
    apiFetch<{ serviceFolderUrl: string }>(`/crm/service/${id}/folder`),

  // ─── Line items (pre-FV pozycje) ──────────────────────────
  listLineItems: (id: string) =>
    apiFetch<ServiceOrderLineItemResponse[]>(`/crm/service/${id}/line-items`),
  saveLineItems: (id: string, items: Array<Partial<ServiceOrderLineItemResponse>>) =>
    apiFetch<{ items: ServiceOrderLineItemResponse[]; replaced: number; deleted: number }>(
      `/crm/service/${id}/line-items`,
      { method: 'PUT', body: JSON.stringify({ items }) }
    ),

  // Upload via FormData. apiFetch sends JSON only, so we build a manual
  // fetch and reuse the same `heizos-auth` localStorage scheme.
  uploadPhotos: async (id: string, files: File[]): Promise<{
    serviceFolderId: string
    serviceFolderUrl: string
    uploaded: Array<{ id: string; name: string; webViewLink: string; thumbnailLink?: string }>
  }> => {
    const fd = new FormData()
    for (const f of files) fd.append('file', f)
    let token: string | null = null
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('heizos-auth') : null
      if (raw) token = JSON.parse(raw)?.state?.token || null
    } catch { /* noop */ }
    const res = await fetch(`/api/crm/service/${id}/photos`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    })
    if (!res.ok) {
      const txt = await res.text().catch(() => '')
      throw new Error(txt || `Upload failed (${res.status})`)
    }
    return res.json()
  },
}

/* ──────────────────────────────────────────
   TECHNICIANS
   ────────────────────────────────────────── */

export const technicianApi = {
  list: () => apiFetch<TechnicianResponse[]>('/crm/technicians'),

  get: (id: string) => apiFetch<TechnicianResponse>(`/crm/technicians/${id}`),

  create: (data: Record<string, unknown>) =>
    apiFetch<TechnicianResponse>('/crm/technicians', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<TechnicianResponse>(`/crm/technicians/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<{ ok: boolean }>(`/crm/technicians/${id}`, { method: 'DELETE' }),
}

/* ──────────────────────────────────────────
   Response Types (match Prisma models)
   ────────────────────────────────────────── */

interface DealResponse {
  id: string
  name: string
  clientName: string
  clientEmail: string
  clientPhone: string
  clientAddress: string
  type: string
  stage: string
  value: number
  notes: string
  createdAt: string
  updatedAt: string
  nextContactDate: string | null
  lostReason?: string | null
  lostNote?: string | null
  lostDate?: string | null
  wonDate?: string | null
  wonOfferId?: string | null
  montageStartDate?: string | null
  montageEndDate?: string | null
  montageEventId?: string | null
  montageNote?: string | null
  warningNote?: string | null
  materialStatus?: string | null
  auditAuditor?: string | null
  auditDismissed?: boolean | null
  contactOwnerId?: string | null
  contactOwnerName?: string | null
  clientSource: string
  partnerId?: string | null
  partner?: PartnerResponse | null
  lexwareContactId?: string | null
  gdriveFolderId?: string | null
  gdriveFolderUrl?: string | null
  gdriveSubfolders?: Record<string, { id: string; url: string }> | null
  documents: DocumentResponse[]
  history: HistoryEventResponse[]
  offerItems: OfferItemResponse[]
  optionalItems?: OfferItemResponse[]
}

interface DocumentResponse {
  id: string
  name: string
  type: string
  url: string
  uploadedAt: string
}

interface HistoryEventResponse {
  id: string
  type: string
  description: string
  timestamp: string
  auto: boolean
}

interface OfferItemResponse {
  id: string
  productId: string
  productName: string
  description?: string | null
  quantity: number
  ekPrice: number
  vkPrice: number
  unit: string
  taxRate: number
  lineItemType: string
  realCost?: number | null
  sortOrder: number
}

interface OfferResponse {
  id: string
  dealId: string
  offerNumber: string
  voucherDate: string
  validUntil: string
  language: string
  title: string
  introduction: string
  remark: string
  taxType: string
  totalDiscount: number
  paymentTermsDays: number
  status: string
  createdAt: string
  updatedAt: string
  addressName: string
  addressSupplement?: string | null
  addressStreet: string
  addressZip: string
  addressCity: string
  addressCountry: string
  addressContactId?: string | null
  lineItems: unknown[]
  lexwareVoucherId?: string | null
  lexwareSyncedAt?: string | null
}

interface ProductResponse {
  id: string
  name: string
  category: string
  sku: string
  ekPrice: number
  vkPrice: number
  unit: string
  description: string
  inStock: boolean
  sortOrder: number
}

interface CategoryResponse {
  id: string
  label: string
  sortOrder: number
}

interface PartnerResponse {
  id: string
  name: string
  logoUrl?: string | null
}

interface TemplateResponse {
  id: string
  name: string
  description: string
  items: unknown[]
  createdAt: string
}

export const calendarApi = {
  list: (from: string, to: string) =>
    apiFetch<{ from: string; to: string; count: number; events: CalendarEventResponse[] }>(`/crm/calendar?from=${from}&to=${to}`),
  create: (data: Record<string, unknown>) =>
    apiFetch<Record<string, unknown>>('/crm/calendar', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: Record<string, unknown>) =>
    apiFetch<Record<string, unknown>>('/crm/calendar', { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id: string) =>
    apiFetch<{ ok: boolean }>(`/crm/calendar?id=${id}`, { method: 'DELETE' }),
}

// Grafik zespolu (zakladka "Zespol" w Kalendarzu) — wpisy reczne + definicje teamow.
export const scheduleApi = {
  list: (from: string, to: string) =>
    apiFetch<{ people: { id: string; name: string; initials: string; role: string }[]; entries: Record<string, unknown>[]; teams: Record<string, unknown>[] }>(`/crm/schedule?from=${from}&to=${to}`),
  addEntry: (data: Record<string, unknown>) =>
    apiFetch<Record<string, unknown>>('/crm/schedule', { method: 'POST', body: JSON.stringify(data) }),
  removeEntry: (id: string) =>
    apiFetch<{ ok: boolean }>(`/crm/schedule?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),
  saveTeam: (data: Record<string, unknown>) =>
    apiFetch<Record<string, unknown>>('/crm/teams', { method: 'POST', body: JSON.stringify(data) }),
  removeTeam: (id: string) =>
    apiFetch<{ ok: boolean }>(`/crm/teams?id=${encodeURIComponent(id)}`, { method: 'DELETE' }),
}

// Finanse WDE (tylko admin): bilans roczny Lexware+arkusz FV.
export const financeApi = {
  wdeYear: (year: number, refresh = false) =>
    apiFetch<Record<string, unknown>>(`/crm/finance/wde?year=${year}${refresh ? '&refresh=1' : ''}`),
}

export const auditApi = {
  // PATCH terminu audytu (HeizlastProject) — przyjmuje datę lub datę+godzinę ISO.
  setDate: (projectId: string, auditDate: string) =>
    apiFetch<Record<string, unknown>>(`/heizlast/${projectId}/audit-date`, { method: 'PATCH', body: JSON.stringify({ auditDate }) }),
}

export interface CalendarEventResponse {
  id: string
  source: 'montage' | 'service' | 'audit' | 'task' | 'contact' | 'internal' | 'planning'
  auditProjectId?: string
  title: string
  start: string
  end: string | null
  date: string
  endDate: string | null
  time: string | null
  allDay: boolean
  color: string
  assigneeIds: string[]
  assigneeNames: string[]
  location: string
  lat: number | null
  lng: number | null
  refType: 'deal' | 'service' | 'heizlast' | 'task' | 'calendarEvent'
  refId: string
  status: string
  tentative: boolean
  note?: string
}

interface UserResponse {
  id: string
  name: string
  email: string
  role: string
  active: boolean
  createdAt: string
}

interface ServiceOrderResponse {
  id: string
  clientName: string
  clientEmail: string
  clientPhone: string
  clientAddress: string
  clientId: string | null
  dealId: string | null
  deal: { id: string; name: string; stage: string } | null
  type: string
  status: string
  priority: string
  title: string
  description: string
  scheduledDate: string | null
  scheduledTime: string | null
  completedDate: string | null
  estimatedDuration: number
  recurring: boolean
  recurrenceMonths: number | null
  nextServiceDate: string | null
  technicianNotes: string
  internalNote: string
  technicianId: string | null
  equipmentType: string
  equipmentSerial: string
  equipmentId: string | null
  calendarEventId: string | null

  serviceFolderId: string | null
  serviceFolderUrl: string | null

  closed: boolean
  closedDate: string | null
  closedById: string | null

  lexwareInvoiceId: string | null
  lexwareInvoiceUrl: string | null

  wartungProtokoll?: Record<string, unknown> | null
  protocolSentAt?: string | null

  createdAt: string
  updatedAt: string
}

interface SettingsResponse {
  company: {
    name: string
    street: string
    zip: string
    city: string
    country: string
    phone: string
    email: string
    web: string
    taxId: string
    handelsregister: string
    geschaeftsfuehrer: string
    bankName: string
    iban: string
    bic: string
    logoUrl: string
  }
  smtp: {
    configured: boolean
    host: string
    port: number
    secure: boolean
    user: string
    password: string
    fromName: string
    fromEmail: string
    signature: string
  }
  lexware: {
    configured: boolean
    apiKey: string
    defaultLayoutId?: string | null
    partnerLayoutId?: string | null
    layouts?: { id: string; name: string }[] | null
    partnerLayoutMap?: Record<string, string> | null
  }
  gdrive: {
    connected: boolean
    tokens?: { access_token: string; refresh_token?: string; expiry_date?: number } | null
    rootFolderId?: string | null
    rootFolderName?: string | null
    email?: string | null
    calendarId?: string | null
    calendarName?: string | null
  }
  language: string
}

/* ──────────────────────────────────────────
   EQUIPMENT
   ────────────────────────────────────────── */

export const equipmentApi = {
  list: (params?: { clientId?: string; status?: string; category?: string }) => {
    const query = new URLSearchParams()
    if (params?.clientId) query.set('clientId', params.clientId)
    if (params?.status) query.set('status', params.status)
    if (params?.category) query.set('category', params.category)
    const qs = query.toString()
    return apiFetch<Record<string, unknown>[]>(`/crm/equipment${qs ? '?' + qs : ''}`)
  },
  create: (data: Record<string, unknown>) =>
    apiFetch<Record<string, unknown>>('/crm/equipment', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<Record<string, unknown>>(`/crm/equipment/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiFetch<{ success: boolean }>(`/crm/equipment/${id}`, { method: 'DELETE' }),
}

/* ──────────────────────────────────────────
   CLIENTS
   ────────────────────────────────────────── */

export interface ClientResponse {
  id: string
  name: string
  email: string
  phone: string
  address: string
}

// ── Service Catalog ───────────────────────────────────────
export interface ServiceCatalogItemResponse {
  id: string
  name: string
  description: string
  category: string
  unit: string
  unitPrice: number
  vatRate: number
  active: boolean
  sortOrder: number
}

export const serviceCatalogApi = {
  list: (params?: { active?: boolean }) => {
    const qs = params?.active === false ? '?active=false' : ''
    return apiFetch<ServiceCatalogItemResponse[]>(`/crm/service-catalog${qs}`)
  },
  create: (data: Partial<ServiceCatalogItemResponse>) =>
    apiFetch<ServiceCatalogItemResponse>('/crm/service-catalog', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<ServiceCatalogItemResponse>) =>
    apiFetch<ServiceCatalogItemResponse>(`/crm/service-catalog/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiFetch<{ ok: boolean; softDelete: boolean }>(`/crm/service-catalog/${id}`, {
      method: 'DELETE',
    }),
}

// ── Service Order Line Items ──────────────────────────────
export interface ServiceOrderLineItemResponse {
  id: string
  serviceOrderId: string
  catalogItemId: string | null
  name: string
  description: string
  unit: string
  quantity: number
  unitPrice: number
  position: number
}

export const clientsApi = {
  list: () => apiFetch<ClientResponse[]>('/crm/clients'),
  findOrCreate: (data: { name: string; email?: string; phone?: string; address?: string; lat?: number; lng?: number; createFolder?: boolean }) =>
    apiFetch<ClientResponse>('/crm/clients', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Record<string, unknown>) =>
    apiFetch<ClientResponse>('/crm/clients/' + id, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) =>
    apiFetch<{ ok: boolean }>('/crm/clients/' + id, { method: 'DELETE' }),
}

export interface TechnicianResponse {
  id: string
  companyId: string
  userId: string | null
  name: string
  email: string
  phone: string
  color: string
  avatar: string | null
  region: string
  regionZips: string[]
  homeAddress: string
  homeLat: number | null
  homeLng: number | null
  maxOrdersPerDay: number
  workStartTime: string
  workEndTime: string
  active: boolean
  createdAt: string
  updatedAt: string
  _count?: { serviceOrders: number }
}
